import { captureException } from '@sentry/nuxt';
import { GetAccountBalanceRequest } from 'gportal-grpc/gportal/api/payment/v1/requests_pb';
import { AccountBalance } from 'gportal-grpc/gportal/api/payment/v1/responses_pb';
import { Money } from 'gportal-grpc/gportal/type/v1/money_pb';
import { convertGpRegionToGrpcRegion } from '~/plugins/grpc/universal/utils';

// Payment facts about the active customer that the session customer does not carry.
export const usePaymentStore = defineStore('payment', () => {
  const authStore = useAuthStore();
  const $grpcPayment = useGrpcPayment();
  const buildAuthMetadata = useAuthMetadata();

  const state = reactive({
    balanceMigrated: false,
    // The Chargebee buckets behind the wallet, one per currency, from the same
    // GetAccountBalance read as the flag (a cached display path).
    balances: [] as AccountBalance[],
    // Which customer and region the flag belongs to, so a region switch never shows the old answer.
    balanceMigratedFor: null as string | null,
  });

  let pending: Promise<void> | null = null;

  const owner = computed(() =>
    authStore.user?.id && authStore.activeRegion
      ? `${authStore.user.id}|${authStore.activeRegion}`
      : null,
  );

  async function fetchBalanceMigrated(): Promise<void> {
    while (pending != null) await pending;
    const key = owner.value;
    if (key == null || state.balanceMigratedFor === key) return;

    pending = (async () => {
      const { response, error } = await $grpcPayment.executeApiCallActive(
        'getAccountBalance',
        new GetAccountBalanceRequest({
          customerKeycloakId: authStore.user?.id,
          region: convertGpRegionToGrpcRegion(authStore.activeRegion!),
        }),
        buildAuthMetadata(),
      );
      if (error != null) {
        captureException(error, {
          extra: { context: 'payment.fetchBalanceMigrated' },
        });
        return;
      }
      state.balanceMigrated = response?.balanceMigrated ?? false;
      state.balances = response?.balances ?? [];
      state.balanceMigratedFor = key;
      // The wallet shown everywhere else is this read's net balance too, so it
      // follows: a migrated customer's total and sidebar match the breakdown.
      const net = activeAccountBalance.value?.netBalance;
      if (net && authStore.activeRegion && owner.value === key) {
        authStore.syncActiveWalletAmount(
          authStore.activeRegion,
          net.currencyCode,
          BigInt(net.amount),
        );
      }
    })().finally(() => {
      pending = null;
    });
    await pending;
  }

  // Forget the answer, so the next fetch asks again — after the customer migrated
  // themselves (TV-2188), the cached "no" is wrong.
  function invalidateBalanceMigrated(): void {
    state.balanceMigratedFor = null;
  }

  const isBalanceMigrated = computed(
    () => state.balanceMigratedFor === owner.value && state.balanceMigrated,
  );

  // The active customer's buckets in their wallet currency, or null when not
  // migrated or not loaded yet. A migrated customer with no bucket in that
  // currency holds nothing in it — Chargebee answers `balances: []` for an
  // all-zero account — so that is a zero balance, not a missing one: without
  // it the Payments page hid the whole breakdown from every new customer.
  const activeAccountBalance = computed<AccountBalance | null>(() => {
    if (!isBalanceMigrated.value) return null;
    const wallet = authStore.activeCustomer?.wallet;
    const currency = wallet?.currencyCode;
    if (!currency) return null;
    return (
      state.balances.find((balance) => balance.currencyCode === currency) ??
      zeroBalance(currency, wallet?.fraction ?? 2)
    );
  });

  // Whether a migrated customer owes open invoices. Read off the invoice dues,
  // not the wallet's sign: the wallet also counts promotional credit
  // (TV-2194), which can keep it above zero while an invoice is unpaid. Until
  // the buckets are loaded, a wallet below zero still can only mean debt.
  const hasUnpaidInvoices = computed(() => {
    if (!isBalanceMigrated.value) return false;
    const due = activeAccountBalance.value?.outstandingDue;
    if (due) return BigInt(due.amount) > 0n;
    const wallet = authStore.activeCustomer?.wallet;
    return wallet != null && BigInt(wallet.amount) < 0n;
  });

  return {
    ...toRefs(state),
    fetchBalanceMigrated,
    invalidateBalanceMigrated,
    isBalanceMigrated,
    activeAccountBalance,
    hasUnpaidInvoices,
  };
});

// An account balance of nothing in one currency.
function zeroBalance(currencyCode: string, fraction: number): AccountBalance {
  const zero = () => new Money({ amount: 0n, currencyCode, fraction });
  return new AccountBalance({
    currencyCode,
    excessPayments: zero(),
    refundableCredits: zero(),
    promotionalCredits: zero(),
    outstandingDue: zero(),
    availableBalance: zero(),
    netBalance: zero(),
  });
}
